import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

// Leitor de QR Code / EAN / códigos de barras para a digitação mobile de inventário.
// Encapsula a lib html5-qrcode (câmera traseira preferencial, qrbox central sombreado,
// leitura automática, feedback sonoro/vibração, lanterna quando suportada).
@Component({
  selector: 'app-inventario-scanner-mobile',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  templateUrl: './inventario-scanner-mobile.component.html',
  styleUrl: './inventario-scanner-mobile.component.css',
})
export class InventarioScannerMobileComponent implements AfterViewInit, OnDestroy {
  @Output() codigoLido = new EventEmitter<string>();
  @Output() fechar = new EventEmitter<void>();

  public readerId = 'inv-scanner-' + Math.floor(Math.random() * 1_000_000);
  public erro = '';
  public iniciando = true;
  public torchSuportada = false;
  public torchLigada = false;

  private scanner?: Html5Qrcode;
  private pausado = false;
  private ultimoCodigo = '';
  private ultimoCodigoEm = 0;
  private readonly onResize = () => this.aplicarCover();

  ngAfterViewInit(): void {
    // pequeno atraso para garantir que o elemento do reader já está no DOM
    setTimeout(() => this.iniciar(), 50);
  }

  ngOnDestroy(): void {
    this.parar();
  }

  private async iniciar(): Promise<void> {
    try {
      this.scanner = new Html5Qrcode(this.readerId, {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
        ],
        verbose: false,
        // Usa a API nativa BarcodeDetector do navegador quando disponível
        // (Chrome / WebView Android). É decodificada em C++/hardware, muito mais
        // rápida e tolerante que o ZXing em JS embutido — é o que torna o
        // scanapp.org praticamente instantâneo. Sem isso, a lib cai no motor lento.
        // (campo de topo + experimentalFeatures cobrem as duas APIs da v2.3.x)
        useBarCodeDetectorIfSupported: true,
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true,
        },
      });

      await this.scanner.start(
        // videoConstraints (abaixo) tem precedência; mantemos facingMode lá.
        { facingMode: 'environment' },
        {
          fps: 15,
          // Sem qrbox: varremos o frame inteiro. Como o vídeo usa object-fit: cover,
          // a região recortada pela lib (resolução intrínseca) não bate com a moldura
          // que o usuário vê. Varrendo tudo, qualquer código visível na tela é lido —
          // acaba o desalinhamento entre a moldura vermelha e a área realmente lida.
          // Pede o stream em resolução alta + foco contínuo para frames nítidos,
          // essenciais para códigos densos ou um pouco distantes.
          // Pede um stream em RETRATO (alto) para o vídeo preencher a tela do
          // celular sem precisar distorcer. Se o aparelho ignorar e entregar
          // paisagem, o vídeo vira uma faixa central — sem distorção, ainda lê.
          videoConstraints: {
            facingMode: 'environment',
            width: { ideal: 1080 },
            height: { ideal: 1920 },
            advanced: [{ focusMode: 'continuous' } as any],
          } as MediaTrackConstraints,
        },
        (decodedText: string) => this.onLeitura(decodedText),
        () => { /* falhas de frame são esperadas e ignoradas */ }
      );

      this.iniciando = false;
      this.detectarTorch();
      this.ativarAutofoco();
      // aguarda o layout do vídeo e aplica o cover (tela cheia sem distorcer a leitura)
      requestAnimationFrame(() => requestAnimationFrame(() => this.aplicarCover()));
      setTimeout(() => this.aplicarCover(), 300); // fallback caso o vídeo ainda não tenha medidas
      window.addEventListener('resize', this.onResize);
      window.addEventListener('orientationchange', this.onResize);
    } catch (e: any) {
      this.iniciando = false;
      this.erro = this.mensagemErroCamera(e);
    }
  }

  private mensagemErroCamera(e: any): string {
    const nome = e?.name || e?.toString() || '';
    if (/NotAllowed|Permission/i.test(nome)) return 'Permissão de câmera negada. Habilite o acesso à câmera no navegador.';
    if (/NotFound|Devices/i.test(nome)) return 'Nenhuma câmera encontrada neste dispositivo.';
    if (/NotReadable|TrackStart/i.test(nome)) return 'A câmera está em uso por outro aplicativo.';
    return 'Não foi possível iniciar a câmera. Verifique as permissões e use HTTPS.';
  }

  // Liga o autofoco da câmera. O focusMode passado na inicialização costuma ser
  // ignorado por muitos aparelhos; aplicá-lo explicitamente após o start é mais
  // confiável. Preferimos 'continuous' (foco contínuo); se indisponível, tentamos
  // 'auto'/'single-shot'. Falhas são silenciosas — nem todo device expõe focusMode.
  private async ativarAutofoco(): Promise<void> {
    try {
      const caps: any = this.scanner?.getRunningTrackCapabilities?.();
      const modos: string[] = caps?.focusMode || [];
      if (!Array.isArray(modos) || modos.length === 0) return;

      const preferido =
        ['continuous', 'auto', 'single-shot'].find((m) => modos.includes(m)) || modos[0];
      if (!preferido) return;

      await this.scanner?.applyVideoConstraints({
        advanced: [{ focusMode: preferido } as any],
      } as any);
    } catch {
      /* dispositivo não suporta controle de foco — usa o foco automático padrão */
    }
  }

  // Faz o vídeo cobrir a tela inteira (igual ao scanapp.org) SEM quebrar a leitura.
  // Usamos transform: scale, que é puramente VISUAL: clientWidth/clientHeight do vídeo
  // — dimensões que a html5-qrcode usa para recortar/amostrar o frame — NÃO são
  // afetadas por transform (são propriedades de layout). Assim a decodificação continua
  // com a proporção correta enquanto a imagem é ampliada para preencher a viewport.
  private aplicarCover(): void {
    const container = document.getElementById(this.readerId);
    const video = container?.querySelector('video') as HTMLVideoElement | null;
    const viewport = container?.parentElement; // .scanner-viewport (ocupa a tela toda)
    if (!video || !viewport || !video.clientWidth || !video.clientHeight) return;

    const escala = Math.max(
      viewport.clientWidth / video.clientWidth,
      viewport.clientHeight / video.clientHeight,
    );
    video.style.transformOrigin = 'center center';
    video.style.transform = escala > 1 ? `scale(${escala})` : 'none';
  }

  private detectarTorch(): void {
    try {
      const caps: any = this.scanner?.getRunningTrackCapabilities?.();
      this.torchSuportada = !!caps && 'torch' in caps;
    } catch {
      this.torchSuportada = false;
    }
  }

  private onLeitura(texto: string): void {
    if (this.pausado || !texto) return;
    const agora = Date.now();
    // evita reprocessar o mesmo código em sequência (debounce por código)
    if (texto === this.ultimoCodigo && agora - this.ultimoCodigoEm < 2500) return;
    this.ultimoCodigo = texto;
    this.ultimoCodigoEm = agora;

    this.feedback();
    this.codigoLido.emit(texto);
  }

  // Chamado pelo pai quando o painel inferior abre/fecha, para pausar/retomar leituras.
  public pausar(): void {
    this.pausado = true;
  }

  public retomar(): void {
    this.pausado = false;
    this.ultimoCodigo = '';
  }

  public async alternarTorch(): Promise<void> {
    if (!this.scanner || !this.torchSuportada) return;
    try {
      this.torchLigada = !this.torchLigada;
      await this.scanner.applyVideoConstraints({
        advanced: [{ torch: this.torchLigada } as any],
      } as any);
    } catch {
      this.torchLigada = false;
    }
  }

  public onFechar(): void {
    this.fechar.emit();
  }

  private feedback(): void {
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(120);
    } catch { /* ignore */ }
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.value = 0.05;
      osc.start();
      setTimeout(() => { osc.stop(); ctx.close(); }, 120);
    } catch { /* ignore */ }
  }

  private async parar(): Promise<void> {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    if (!this.scanner) return;
    try {
      await this.scanner.stop();
      this.scanner.clear();
    } catch { /* ignore */ }
    this.scanner = undefined;
  }
}
